import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Tag,
  Button,
  Modal,
  Input,
  Space,
  Typography,
  message,
  Tooltip,
  Timeline,
  theme,
} from 'antd';
import {
  PlusOutlined,
  CheckOutlined,
  CloseOutlined,
  SendOutlined,
  EyeOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import { apiService } from '../services/api';
import type { ScopeVersion } from '../services/api';
import { useTheme } from '../hooks/useTheme';

const { TextArea } = Input;
const { Text } = Typography;

interface ScopeVersionHistoryProps {
  projectId: number;
  currentScope?: string;
  onScopeUpdated?: () => void;
  readOnly?: boolean;
  canPropose?: boolean; // Allow clients to propose scope changes
  isClientView?: boolean; // Different UI labels for client vs admin
}

const statusColors: Record<string, string> = {
  draft: 'default',
  pending_approval: 'processing',
  approved: 'success',
  rejected: 'error',
};

const statusLabels: Record<string, string> = {
  draft: 'Draft',
  pending_approval: 'Pending Approval',
  approved: 'Approved',
  rejected: 'Rejected',
};

export const ScopeVersionHistory: React.FC<ScopeVersionHistoryProps> = ({
  projectId,
  currentScope,
  onScopeUpdated,
  readOnly = false,
  canPropose = false,
  isClientView = false,
}) => {
  // canPropose allows creating drafts and submitting for approval (client feature)
  // !readOnly allows full admin actions (approve, reject)
  const { token } = theme.useToken();
  const { isDarkMode } = useTheme();
  const [versions, setVersions] = useState<ScopeVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<ScopeVersion | null>(null);
  const [newScopeContent, setNewScopeContent] = useState('');
  const [changeNotes, setChangeNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchVersions = async () => {
    try {
      setLoading(true);
      const data = await apiService.getScopeHistory(projectId);
      setVersions(data);
    } catch (error) {
      message.error('Failed to load scope versions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVersions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const handleCreateDraft = async (submitImmediately = false) => {
    if (!newScopeContent.trim()) {
      message.error('Scope content is required');
      return;
    }
    try {
      setActionLoading(true);
      const response = await apiService.createScopeDraft(projectId, newScopeContent, changeNotes);
      
      // Handle both wrapped and unwrapped responses
      const draft = (response as any)?.data || response;
      const draftId = draft?.id;
      
      // If client is proposing, automatically submit for approval
      if (submitImmediately && draftId) {
        await apiService.submitScopeForApproval(draftId);
        message.success('Scope change proposal submitted for approval');
      } else if (submitImmediately) {
        message.warning('Draft created but could not auto-submit. Please submit manually.');
      } else {
        message.success('Draft created successfully');
      }
      
      setCreateModalVisible(false);
      setNewScopeContent('');
      setChangeNotes('');
      fetchVersions();
    } catch (error) {
      message.error('Failed to create scope proposal');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubmitForApproval = async (versionId: number) => {
    try {
      setActionLoading(true);
      await apiService.submitScopeForApproval(versionId);
      message.success('Submitted for approval');
      fetchVersions();
    } catch (error) {
      message.error('Failed to submit for approval');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async (versionId: number) => {
    try {
      setActionLoading(true);
      await apiService.approveScopeVersion(versionId);
      message.success('Scope version approved');
      fetchVersions();
      onScopeUpdated?.();
    } catch (error) {
      message.error('Failed to approve');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedVersion || !rejectionReason.trim()) {
      message.error('Rejection reason is required');
      return;
    }
    try {
      setActionLoading(true);
      await apiService.rejectScopeVersion(selectedVersion.id, rejectionReason);
      message.success('Scope version rejected');
      setRejectModalVisible(false);
      setRejectionReason('');
      setSelectedVersion(null);
      fetchVersions();
    } catch (error) {
      message.error('Failed to reject');
    } finally {
      setActionLoading(false);
    }
  };

  const openCreateModal = () => {
    // Try to get scope from currentScope prop first, then fall back to versions
    let scopeToEdit = currentScope || '';
    if (!scopeToEdit) {
      // First try approved version
      const latestApproved = versions.find(v => v.versionStatus === 'approved');
      if (latestApproved) {
        scopeToEdit = latestApproved.scopeContent || '';
      } else {
        // Fall back to pending_approval or draft (versions are sorted desc by versionNumber)
        const latestPending = versions.find(v => 
          v.versionStatus === 'pending_approval' || v.versionStatus === 'draft'
        );
        if (latestPending) {
          scopeToEdit = latestPending.scopeContent || '';
        }
      }
    }
    setNewScopeContent(scopeToEdit);
    setChangeNotes('');
    setCreateModalVisible(true);
  };

  const columns = [
    {
      title: 'Version',
      dataIndex: 'versionNumber',
      key: 'versionNumber',
      width: 80,
      render: (v: number) => <Text strong>v{v}</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'versionStatus',
      key: 'versionStatus',
      width: 150,
      render: (status: string) => (
        <Tag color={statusColors[status]}>{statusLabels[status]}</Tag>
      ),
    },
    {
      title: 'Change Notes',
      dataIndex: 'changeNotes',
      key: 'changeNotes',
      ellipsis: true,
      render: (notes: string) => notes || <Text type="secondary">No notes</Text>,
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Acknowledged',
      dataIndex: 'acknowledgedByClient',
      key: 'acknowledgedByClient',
      width: 120,
      render: (ack: boolean, record: ScopeVersion) =>
        record.versionStatus === 'approved' ? (
          ack ? (
            <Tag color="green">Yes</Tag>
          ) : (
            <Tag color="orange">Pending</Tag>
          )
        ) : (
          '-'
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_: any, record: ScopeVersion) => (
        <Space size="small">
          <Tooltip title="View">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => {
                setSelectedVersion(record);
                setViewModalVisible(true);
              }}
            />
          </Tooltip>
          {/* Admin can submit drafts for approval */}
          {!readOnly && record.versionStatus === 'draft' && (
            <Tooltip title="Submit for Approval">
              <Button
                type="text"
                icon={<SendOutlined />}
                onClick={() => handleSubmitForApproval(record.id)}
                loading={actionLoading}
              />
            </Tooltip>
          )}
          {/* Admin can approve/reject pending versions */}
          {!readOnly && record.versionStatus === 'pending_approval' && (
            <>
              <Tooltip title="Approve">
                <Button
                  type="text"
                  icon={<CheckOutlined />}
                  style={{ color: '#52c41a' }}
                  onClick={() => handleApprove(record.id)}
                  loading={actionLoading}
                />
              </Tooltip>
              <Tooltip title="Reject">
                <Button
                  type="text"
                  icon={<CloseOutlined />}
                  danger
                  onClick={() => {
                    setSelectedVersion(record);
                    setRejectModalVisible(true);
                  }}
                />
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  const canCreateNew = !readOnly || canPropose;

  return (
    <Card
      title={
        <Space>
          <HistoryOutlined />
          <span>Scope Version History</span>
        </Space>
      }
      extra={
        canCreateNew && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
            {isClientView ? 'Propose Scope Change' : 'New Version'}
          </Button>
        )
      }
    >
      <Table
        dataSource={versions}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 5 }}
        size="small"
      />

      {/* Create New Version Modal */}
      <Modal
        title={isClientView ? 'Propose Scope Change' : 'Create New Scope Version'}
        open={createModalVisible}
        onOk={() => handleCreateDraft(isClientView)} // Auto-submit for clients
        onCancel={() => setCreateModalVisible(false)}
        confirmLoading={actionLoading}
        okText={isClientView ? 'Submit Proposal' : 'Create Draft'}
        width={800}
      >
        {isClientView && (
          <div style={{ marginBottom: 16, padding: 12, background: isDarkMode ? token.colorBgElevated : token.colorInfoBg, borderRadius: 4, border: `1px solid ${isDarkMode ? token.colorBorder : token.colorInfoBorder}`, color: token.colorText }}>
            <Text style={{ color: token.colorText }}>
              Your proposed scope change will be submitted for review. The project team will review and approve or reject your proposal.
            </Text>
          </div>
        )}
        <div style={{ marginBottom: 16 }}>
          <Text strong>{isClientView ? 'Reason for Change' : 'Change Notes'}</Text>
          <Input
            placeholder={isClientView ? 'Please explain why you are proposing this change...' : 'Brief description of changes...'}
            value={changeNotes}
            onChange={(e) => setChangeNotes(e.target.value)}
            style={{ marginTop: 8 }}
          />
        </div>
        <div>
          <Text strong>{isClientView ? 'Proposed Scope' : 'Scope Content'}</Text>
          <TextArea
            rows={12}
            value={newScopeContent}
            onChange={(e) => setNewScopeContent(e.target.value)}
            placeholder="Enter the project scope..."
            style={{ marginTop: 8 }}
          />
        </div>
      </Modal>

      {/* View Version Modal */}
      <Modal
        title={`Scope Version ${selectedVersion?.versionNumber}`}
        open={viewModalVisible}
        onCancel={() => {
          setViewModalVisible(false);
          setSelectedVersion(null);
        }}
        footer={null}
        width={800}
      >
        {selectedVersion && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Tag color={statusColors[selectedVersion.versionStatus]}>
                {statusLabels[selectedVersion.versionStatus]}
              </Tag>
              {selectedVersion.acknowledgedByClient && (
                <Tag color="green">Acknowledged by Client</Tag>
              )}
            </div>

            {selectedVersion.changeNotes && (
              <div style={{ marginBottom: 16 }}>
                <Text strong>Change Notes:</Text>
                <div
                  style={{
                    padding: 12,
                    background: isDarkMode ? token.colorBgElevated : token.colorBgLayout,
                    borderRadius: 4,
                    marginTop: 8,
                    border: `1px solid ${token.colorBorder}`,
                    color: token.colorText,
                  }}
                >
                  {selectedVersion.changeNotes}
                </div>
              </div>
            )}

            {selectedVersion.rejectionReason && (
              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ color: '#ff4d4f' }}>
                  Rejection Reason:
                </Text>
                <div
                  style={{
                    padding: 12,
                    background: '#fff2f0',
                    borderRadius: 4,
                    marginTop: 8,
                    border: '1px solid #ffccc7',
                  }}
                >
                  {selectedVersion.rejectionReason}
                </div>
              </div>
            )}

            <div>
              <Text strong>Scope Content:</Text>
              <div
                style={{
                  padding: 16,
                  background: isDarkMode ? token.colorBgElevated : token.colorBgLayout,
                  borderRadius: 4,
                  marginTop: 8,
                  maxHeight: 400,
                  overflow: 'auto',
                  border: `1px solid ${token.colorBorder}`,
                  color: token.colorText,
                }}
                dangerouslySetInnerHTML={{ __html: selectedVersion.scopeContent }}
              />
            </div>

            <Timeline
              style={{ marginTop: 24 }}
              items={[
                {
                  color: 'blue',
                  children: (
                    <div>
                      <Text strong>Created</Text>
                      <div>
                        <Text type="secondary">
                          {new Date(selectedVersion.createdAt).toLocaleString()}
                        </Text>
                      </div>
                    </div>
                  ),
                },
                ...(selectedVersion.approvalDate
                  ? [
                      {
                        color:
                          selectedVersion.versionStatus === 'approved' ? 'green' : 'red',
                        children: (
                          <div>
                            <Text strong>
                              {selectedVersion.versionStatus === 'approved'
                                ? 'Approved'
                                : 'Rejected'}
                            </Text>
                            <div>
                              <Text type="secondary">
                                {new Date(selectedVersion.approvalDate).toLocaleString()}
                              </Text>
                            </div>
                          </div>
                        ),
                      },
                    ]
                  : []),
                ...(selectedVersion.acknowledgementDate
                  ? [
                      {
                        color: 'green',
                        children: (
                          <div>
                            <Text strong>Acknowledged by Client</Text>
                            <div>
                              <Text type="secondary">
                                {new Date(
                                  selectedVersion.acknowledgementDate
                                ).toLocaleString()}
                              </Text>
                            </div>
                          </div>
                        ),
                      },
                    ]
                  : []),
              ]}
            />
          </div>
        )}
      </Modal>

      {/* Reject Modal */}
      <Modal
        title="Reject Scope Version"
        open={rejectModalVisible}
        onOk={handleReject}
        onCancel={() => {
          setRejectModalVisible(false);
          setRejectionReason('');
          setSelectedVersion(null);
        }}
        confirmLoading={actionLoading}
      >
        <div>
          <Text>Please provide a reason for rejection:</Text>
          <TextArea
            rows={4}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Reason for rejection..."
            style={{ marginTop: 8 }}
          />
        </div>
      </Modal>
    </Card>
  );
};
